/**
 * Tracking probe, for scripts/perf-track.mjs. Off unless the page was opened
 * with ?perf, and then it only counts: nothing is sent anywhere and nothing is
 * kept after the page closes.
 *
 * The camera loop calls frame() once per tracking result it consumed. The
 * harness reads snapshot() after a warm up, and reset() starts a new window.
 * The same counting runs in the old main thread loop and the new worker loop,
 * so a before and after number are measured the same way.
 */

const ON = (() => { try { return new URLSearchParams(location.search).has('perf'); } catch { return false; } })();

const CAP = 20000;
const inFrame = (p) => p && (p.visibility ?? 1) >= 0.5 && p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;

function create() {
  const s = {};
  let boot = null;   // the tracking client's start-up timings, when the page shares them
  const reset = () => Object.assign(s, {
    since: performance.now(), frames: 0, loop: [], lat: [], player: 0, wrist: 0, bodies: 0,
    handsWhenWrist: 0, hands: 0, shots: 0, cropped: 0, path: s.path || 'main', captured: 0, dropped: 0,
    handRuns: 0, handHits: 0, handRunsWrist: 0, handHitsWrist: 0, extraMs: [], poseMs: [], handsMs: [], steps: [],
  });
  reset();
  const handRun = (fresh, wrist) => {
    if (fresh === null || fresh === undefined) return;
    s.handRuns++; if (fresh > 0) s.handHits++;
    if (wrist) { s.handRunsWrist++; if (fresh > 0) s.handHitsWrist++; }
  };
  return {
    /**
     * One pose result the page used. `ms` is main thread time spent on it,
     * `hands` the hands drawn, `fresh` the hands the hand model found on this
     * frame (null when it did not run on this frame), `bodies` how many bodies
     * the pose model found, whoever they are.
     */
    frame({ ms = 0, lm = null, hands = 0, fresh = null, shot = false, cropped = false, lat = null, poseMs = null, handsMs = null, bodies = 0 } = {}) {
      s.frames++;
      if (bodies > 0) s.bodies++;
      if (poseMs !== null && s.poseMs.length < CAP) s.poseMs.push(poseMs);
      if (handsMs !== null && s.handsMs.length < CAP) s.handsMs.push(handsMs);
      if (s.loop.length < CAP) s.loop.push(ms);
      if (lat !== null && s.lat.length < CAP) s.lat.push(lat);
      if (lm) s.player++;
      const wrist = Boolean(lm) && (inFrame(lm[15]) || inFrame(lm[16]));
      if (wrist) { s.wrist++; if (hands > 0) s.handsWhenWrist++; }
      if (hands > 0) s.hands++;
      if (shot) s.shots++;
      if (cropped) s.cropped++;
      handRun(fresh, wrist);
    },
    /** A result that ran only the hand model. `lm` is the player's last pose. */
    handsOnly({ ms = 0, lm = null, fresh = 0, handsMs = null } = {}) {
      if (s.extraMs.length < CAP) s.extraMs.push(ms);
      if (handsMs !== null && s.handsMs.length < CAP) s.handsMs.push(handsMs);
      handRun(fresh, Boolean(lm) && (inFrame(lm[15]) || inFrame(lm[16])));
    },
    /** The engine's own timings for one result (trackengine.js out.ms), for a breakdown. */
    step(ms) { if (s.steps.length < 2000) s.steps.push(ms); },
    /** Why the shot detector passed on a frame (scoring.js last.skip), counted by reason. */
    skip(reason) { if (reason) { s.skips ??= {}; s.skips[reason] = (s.skips[reason] || 0) + 1; } },
    /** A frame handed to the model, or one skipped because the model was busy. */
    captured() { s.captured++; },
    dropped() { s.dropped++; },
    set path(p) { s.path = p; },
    get path() { return s.path; },
    /** `fn` returns the tracking client's start-up timings (trackclient.js timings). */
    bootFrom(fn) { boot = fn; },
    reset,
    snapshot() { return { ...s, loop: s.loop.slice(), lat: s.lat.slice(), extraMs: s.extraMs.slice(), poseMs: s.poseMs.slice(), handsMs: s.handsMs.slice(), steps: s.steps.slice(), windowMs: performance.now() - s.since, boot: boot ? boot() : null }; },
  };
}

export const probe = ON ? create() : null;
if (probe) { try { window.__hbTrack = probe; } catch { /* not a window */ } }
